import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Snackbar,
} from '@mui/material';

import { useCallback, useEffect, useRef, useState } from 'react';

import {
  downloadMixPlanningFile,
  getApiErrorMessage,
  getLatestMixImport,
  getLatestMixStockSnapshot,
  getMixImport,
  importMixDeliveries,
  importMixStock,
  resumeMixImport,
} from '../../../../api/mix-deliveriesApi';

import {
  MIX_IMPORT_STATUS,
  MIX_IMPORT_STATUS_LABELS,
  MIX_PRODUCT_ISSUE_LABELS,
} from '../types/mix-deliveries.types';

import ConfirmFullImportDialog from './ConfirmFullImportDialog';

export default function MixImportPanel({
  onAddProduct,
  onAnalysisCompleted,
  refreshKey = 0,
}) {
  const stockInputRef = useRef(null);
  const deliveriesInputRef = useRef(null);

  const [stockSnapshot, setStockSnapshot] = useState(null);

  const [importSummary, setImportSummary] = useState(null);

  const [stockFile, setStockFile] = useState(null);

  const [deliveriesFile, setDeliveriesFile] = useState(null);

  const [initialLoading, setInitialLoading] = useState(true);

  const [activeAction, setActiveAction] = useState(null);

  const [error, setError] = useState(null);

  const [fullImportDialogOpen, setFullImportDialogOpen] = useState(false);

  const [notification, setNotification] = useState({
    open: false,
    severity: 'success',
    message: '',
  });

  const isBusy = initialLoading || Boolean(activeAction);

  const showNotification = (message, severity = 'success') => {
    setNotification({
      open: true,
      severity,
      message,
    });
  };

  const loadInitialData = useCallback(async () => {
    setInitialLoading(true);
    setError(null);

    try {
      const [latestStock, latestImport] = await Promise.all([
        getLatestMixStockSnapshot(),
        getLatestMixImport(),
      ]);

      setStockSnapshot(latestStock ?? null);

      setImportSummary(latestImport ?? null);
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setInitialLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData, refreshKey]);

  const handleStockFileChange = (event) => {
    const file = event.target.files?.[0] ?? null;

    setStockFile(file);
    setError(null);
  };

  const handleDeliveriesFileChange = (event) => {
    const file = event.target.files?.[0] ?? null;

    setDeliveriesFile(file);
    setError(null);
  };

  const handleStockImport = async () => {
    if (!stockFile) {
      setError('Wybierz plik zapasu.');

      return;
    }

    setActiveAction('stock-import');
    setError(null);

    try {
      const result = await importMixStock(stockFile);

      setStockSnapshot(result.snapshot);
      showNotification('Zapas został zaimportowany.');

      setStockFile(null);

      if (stockInputRef.current) {
        stockInputRef.current.value = '';
      }
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setActiveAction(null);
    }
  };

  const handleRequestDeliveriesImport = () => {
    if (!deliveriesFile) {
      setError('Wybierz plik dostaw.');

      return;
    }

    if (!stockSnapshot) {
      setError('Najpierw zaimportuj aktualny plik zapasu.');

      return;
    }

    setError(null);
    setFullImportDialogOpen(true);
  };

  const handleConfirmDeliveriesImport = async () => {
    if (!deliveriesFile) {
      setFullImportDialogOpen(false);
      return;
    }

    setActiveAction('deliveries-import');

    setError(null);

    try {
      const result = await importMixDeliveries(deliveriesFile);

      setImportSummary(result);
      setDeliveriesFile(null);
      setFullImportDialogOpen(false);

      if (deliveriesInputRef.current) {
        deliveriesInputRef.current.value = '';
      }

      if (result.status === MIX_IMPORT_STATUS.COMPLETED) {
        showNotification(
          'Import i analiza dostaw zostały zakończone.',
          'success',
        );

        onAnalysisCompleted?.();
      } else if (result.status === MIX_IMPORT_STATUS.WAITING_FOR_PRODUCTS) {
        showNotification(
          'Import został zatrzymany. Uzupełnij brakujące produkty.',
          'warning',
        );
      }
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setActiveAction(null);
    }
  };

  const handleResumeAnalysis = async () => {
    if (!importSummary?.id) {
      return;
    }

    setActiveAction('resume');
    setError(null);

    try {
      const result = await resumeMixImport(importSummary.id);

      setImportSummary(result);

      if (result.status === MIX_IMPORT_STATUS.COMPLETED) {
        showNotification('Analiza została zakończona.');

        onAnalysisCompleted?.();
      } else {
        showNotification('Nadal brakuje danych produktów.', 'warning');
      }
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setActiveAction(null);
    }
  };

  const handlePlanningFile = async () => {
    if (!importSummary?.id) {
      return;
    }

    setActiveAction('planning-file');

    setError(null);

    try {
      await downloadMixPlanningFile(importSummary.id);

      const refreshedImport = await getMixImport(importSummary.id);

      setImportSummary(refreshedImport);
      showNotification('Plik dla planowania został pobrany.');
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setActiveAction(null);
    }
  };

  return (
    <Stack spacing={3}>
      {initialLoading && (
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <CircularProgress size={28} />
        </Paper>
      )}

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'repeat(2, minmax(0, 1fr))',
          },
          gap: 3,
        }}
      >
        <StockImportCard
          stockSnapshot={stockSnapshot}
          selectedFile={stockFile}
          inputRef={stockInputRef}
          disabled={isBusy}
          loading={activeAction === 'stock-import'}
          onFileChange={handleStockFileChange}
          onImport={handleStockImport}
        />

        <DeliveriesImportCard
          stockAvailable={Boolean(stockSnapshot)}
          selectedFile={deliveriesFile}
          inputRef={deliveriesInputRef}
          disabled={isBusy}
          loading={activeAction === 'deliveries-import'}
          onFileChange={handleDeliveriesFileChange}
          onImport={handleRequestDeliveriesImport}
        />
      </Box>

      {importSummary && (
        <ImportResultCard
          summary={importSummary}
          activeAction={activeAction}
          onAddProduct={onAddProduct}
          onResume={handleResumeAnalysis}
          onDownloadPlanning={handlePlanningFile}
        />
      )}
      <ConfirmFullImportDialog
        open={fullImportDialogOpen}
        fileName={deliveriesFile?.name}
        loading={activeAction === 'deliveries-import'}
        onClose={() => setFullImportDialogOpen(false)}
        onConfirm={handleConfirmDeliveriesImport}
      />

      <Snackbar
        open={notification.open}
        autoHideDuration={5000}
        onClose={() =>
          setNotification((current) => ({
            ...current,
            open: false,
          }))
        }
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        <Alert
          severity={notification.severity}
          variant="filled"
          onClose={() =>
            setNotification((current) => ({
              ...current,
              open: false,
            }))
          }
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Stack>
  );
}

function StockImportCard({
  stockSnapshot,
  selectedFile,
  inputRef,
  disabled,
  loading,
  onFileChange,
  onImport,
}) {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="h6">Aktualny zapas</Typography>

            <Typography variant="body2" color="text.secondary">
              Zapas FA, FB oraz strefa kompletacji 0D20.
            </Typography>
          </Box>

          {stockSnapshot ? (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                bgcolor: 'background.default',
              }}
            >
              <Stack spacing={0.75}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  flexWrap="wrap"
                >
                  <Chip size="small" color="success" label="Zapas dostępny" />

                  <Typography variant="body2" fontWeight={600}>
                    {stockSnapshot.fileName}
                  </Typography>
                </Stack>

                <Typography variant="body2">
                  Liczba pozycji: <strong>{stockSnapshot.rowCount}</strong>
                </Typography>

                <Typography variant="caption" color="text.secondary">
                  Zaimportowano: {formatDateTime(stockSnapshot.createdAt)}
                </Typography>
              </Stack>
            </Paper>
          ) : (
            <Alert severity="warning">
              Nie zaimportowano jeszcze aktualnego pliku zapasu.
            </Alert>
          )}

          <Button
            component="label"
            variant="outlined"
            disabled={disabled}
            fullWidth
          >
            Wybierz plik zapasu
            <input
              ref={inputRef}
              hidden
              type="file"
              accept=".xlsx"
              onChange={onFileChange}
            />
          </Button>

          {selectedFile && (
            <Chip
              variant="outlined"
              label={selectedFile.name}
              onDelete={
                disabled
                  ? undefined
                  : () => {
                      if (inputRef.current) {
                        inputRef.current.value = '';
                      }

                      onFileChange({
                        target: {
                          files: [],
                        },
                      });
                    }
              }
              sx={{
                maxWidth: '100%',
                justifyContent: 'space-between',
              }}
            />
          )}

          <Button
            variant="contained"
            fullWidth
            disabled={disabled || !selectedFile}
            onClick={onImport}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Importuj zapas'
            )}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

function DeliveriesImportCard({
  stockAvailable,
  selectedFile,
  inputRef,
  disabled,
  loading,
  onFileChange,
  onImport,
}) {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="h6">Import dostaw</Typography>

            <Typography variant="body2" color="text.secondary">
              Analiza miksów dla 8AAA oraz przygotowanie danych dla 8AAE.
            </Typography>
          </Box>

          {!stockAvailable && (
            <Alert severity="info">
              Import dostaw będzie dostępny po zaimportowaniu zapasu.
            </Alert>
          )}

          {stockAvailable && (
            <Alert severity="success">
              Zapas jest dostępny. Możesz rozpocząć analizę dostaw.
            </Alert>
          )}

          <Button
            component="label"
            variant="outlined"
            disabled={disabled || !stockAvailable}
            fullWidth
          >
            Wybierz plik dostaw
            <input
              ref={inputRef}
              hidden
              type="file"
              accept=".xlsx"
              onChange={onFileChange}
            />
          </Button>

          {selectedFile && (
            <Chip
              variant="outlined"
              label={selectedFile.name}
              onDelete={
                disabled
                  ? undefined
                  : () => {
                      if (inputRef.current) {
                        inputRef.current.value = '';
                      }

                      onFileChange({
                        target: {
                          files: [],
                        },
                      });
                    }
              }
              sx={{
                maxWidth: '100%',
                justifyContent: 'space-between',
              }}
            />
          )}

          <Button
            variant="contained"
            fullWidth
            disabled={disabled || !stockAvailable || !selectedFile}
            onClick={onImport}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Importuj i analizuj'
            )}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

function ImportResultCard({
  summary,
  activeAction,
  onAddProduct,
  onResume,
  onDownloadPlanning,
}) {
  const statusColor = getImportStatusColor(summary.status);

  const canResume =
    summary.status === MIX_IMPORT_STATUS.WAITING_FOR_PRODUCTS ||
    summary.status === MIX_IMPORT_STATUS.FAILED;

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={3}>
          <Stack
            direction={{
              xs: 'column',
              sm: 'row',
            }}
            spacing={1.5}
            justifyContent="space-between"
            alignItems={{
              xs: 'flex-start',
              sm: 'center',
            }}
          >
            <Box>
              <Typography variant="h6">Ostatni import dostaw</Typography>

              <Typography variant="body2" color="text.secondary">
                {summary.deliveryFileName}
              </Typography>
            </Box>

            <Chip
              label={MIX_IMPORT_STATUS_LABELS[summary.status] ?? summary.status}
              color={statusColor}
              variant={statusColor === 'default' ? 'outlined' : 'filled'}
            />
          </Stack>

          {summary.errorMessage && (
            <Alert severity="error">{summary.errorMessage}</Alert>
          )}

          {summary.status === MIX_IMPORT_STATUS.ANALYZING && (
            <Alert severity="info" icon={<CircularProgress size={20} />}>
              Trwa analiza dostaw.
            </Alert>
          )}

          {summary.status === MIX_IMPORT_STATUS.WAITING_FOR_PRODUCTS && (
            <MissingProductsSection
              products={summary.missingProducts ?? []}
              onAddProduct={(product) => onAddProduct?.(product, summary.id)}
            />
          )}

          {canResume && (
            <Button
              variant="contained"
              disabled={Boolean(activeAction)}
              onClick={onResume}
            >
              {activeAction === 'resume' ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                'Wznów analizę'
              )}
            </Button>
          )}

          {summary.status === MIX_IMPORT_STATUS.COMPLETED && (
            <CompletedImportSection
              summary={summary}
              downloading={activeAction === 'planning-file'}
              disabled={Boolean(activeAction)}
              onDownloadPlanning={onDownloadPlanning}
            />
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

function MissingProductsSection({ products, onAddProduct }) {
  return (
    <Stack spacing={2}>
      <Alert severity="warning">
        Analiza została zatrzymana. Uzupełnij brakujące produkty, a następnie
        kliknij „Wznów analizę”.
      </Alert>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Materiał</TableCell>

              <TableCell>Nazwa</TableCell>

              <TableCell>JM</TableCell>

              <TableCell>Problem</TableCell>

              <TableCell align="right">Wystąpienia</TableCell>

              <TableCell align="right">Akcja</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {products.map((product) => (
              <TableRow key={product.id ?? product.materialNumber} hover>
                <TableCell>
                  <Typography fontWeight={600} variant="body2">
                    {product.materialNumber}
                  </Typography>
                </TableCell>

                <TableCell>{product.description}</TableCell>

                <TableCell>{product.unit}</TableCell>

                <TableCell>
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    label={
                      MIX_PRODUCT_ISSUE_LABELS[product.issueType] ??
                      product.issueType
                    }
                  />
                </TableCell>

                <TableCell align="right">{product.occurrenceCount}</TableCell>

                <TableCell align="right">
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={!onAddProduct}
                    onClick={() => onAddProduct?.(product)}
                  >
                    Dodaj produkt
                  </Button>
                </TableCell>
              </TableRow>
            ))}

            {products.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Brak nierozwiązanych produktów.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}

function CompletedImportSection({
  summary,
  downloading,
  disabled,
  onDownloadPlanning,
}) {
  const analysis = summary.analysis ?? {};

  return (
    <Stack spacing={3}>
      <Alert severity="success">
        Analiza dostaw została zakończona pomyślnie.
      </Alert>

      <Divider />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, minmax(0, 1fr))',
            sm: 'repeat(3, minmax(0, 1fr))',
            lg: 'repeat(5, minmax(0, 1fr))',
          },
          gap: 2,
        }}
      >
        <SummaryValue
          label="Dostawy miksowe 8AAA"
          value={analysis.mixDeliveries8AAACount ?? 0}
        />

        <SummaryValue
          label="Pozycje miksowe 8AAA"
          value={analysis.mixItems8AAACount ?? 0}
        />

        <SummaryValue
          label="Dostawy 8AAE"
          value={analysis.planningDeliveries8AAECount ?? 0}
        />

        <SummaryValue
          label="Wiersze dla planowania"
          value={analysis.planningRowsCount ?? 0}
        />

        <SummaryValue
          label="Braki zapasu"
          value={analysis.stockShortagesCount ?? 0}
          severity={analysis.stockShortagesCount > 0 ? 'error' : 'success'}
        />
      </Box>

      {summary.planningFile?.available && (
        <Stack
          direction={{
            xs: 'column',
            sm: 'row',
          }}
          spacing={2}
          alignItems={{
            xs: 'stretch',
            sm: 'center',
          }}
        >
          <Button
            variant="contained"
            disabled={disabled}
            onClick={onDownloadPlanning}
          >
            {downloading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Pobierz plik dla planowania'
            )}
          </Button>

          {summary.planningFile?.generatedAt && (
            <Typography variant="caption" color="text.secondary">
              Ostatnio pobrany:{' '}
              {formatDateTime(summary.planningFile.generatedAt)}
            </Typography>
          )}
        </Stack>
      )}
    </Stack>
  );
}

function SummaryValue({ label, value, severity = 'default' }) {
  const colorMap = {
    default: 'text.primary',
    success: 'success.main',
    warning: 'warning.main',
    error: 'error.main',
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        minWidth: 0,
      }}
    >
      <Typography
        variant="h5"
        fontWeight={700}
        color={colorMap[severity] ?? colorMap.default}
      >
        {value}
      </Typography>

      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Paper>
  );
}

function getImportStatusColor(status) {
  switch (status) {
    case MIX_IMPORT_STATUS.WAITING_FOR_PRODUCTS:
      return 'warning';

    case MIX_IMPORT_STATUS.ANALYZING:
      return 'info';

    case MIX_IMPORT_STATUS.COMPLETED:
      return 'success';

    case MIX_IMPORT_STATUS.FAILED:
      return 'error';

    default:
      return 'default';
  }
}

function formatDateTime(value) {
  if (!value) {
    return '—';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return new Intl.DateTimeFormat('pl-PL', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
}
